import { Link } from 'react-router-dom';
import { useEffect, useState, type JSX } from 'react';
import { fetchBrandsForOffers } from '@/services/slaydAPI/brandOffers';
import useBrandOfferStore from '@/stores/brandOfferStore';
import {
  BRAND_DETAILS_BACK_TO,
  BRAND_DETAILS_COPY,
} from './BrandDetailsPage.constants';
import styles from './BrandDetailsPage.module.css';

const BrandDetailsPage = (): JSX.Element => {
  const { brands, setBrands, brandsLoading, setBrandsLoading } =
    useBrandOfferStore();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (brands.length > 0) return;
    setError(null);
    setBrandsLoading(true);
    fetchBrandsForOffers()
      .then((data) => setBrands(data))
      .catch(() => setError(BRAND_DETAILS_COPY.loadError))
      .finally(() => setBrandsLoading(false));
  }, [brands.length, setBrands, setBrandsLoading]);

  return (
    <div className={styles.page}>
      <div className={styles.shell}>
        <div className={styles.nav}>
          <Link to={BRAND_DETAILS_BACK_TO} className={styles.backBtn}>
            {BRAND_DETAILS_COPY.backLink}
          </Link>
        </div>

        <header className={styles.header}>
          <div className={styles.headerMeta}>
            <span className={styles.idLabel}>{BRAND_DETAILS_COPY.idLabel}</span>
            <span className={styles.typeBadge}>
              {BRAND_DETAILS_COPY.typeBadge}
            </span>
            <span className={`${styles.statusPill} ${styles.statusOn}`}>
              {BRAND_DETAILS_COPY.statusLive}
            </span>
          </div>
          <h1 className={styles.title}>{BRAND_DETAILS_COPY.title}</h1>
          <p className={styles.subtitle}>{BRAND_DETAILS_COPY.subtitle}</p>
        </header>

        <section className={styles.section}>
          <p className={styles.sectionTitle}>
            {BRAND_DETAILS_COPY.overviewSection}
          </p>
          <div className={styles.summaryCard}>
            <p className={styles.summaryLabel}>
              {BRAND_DETAILS_COPY.totalBrandsLabel}
            </p>
            <p className={styles.summaryValue}>{brands.length}</p>
          </div>
        </section>

        {error ? (
          <div className={styles.centeredState}>
            <p className={styles.errorText}>{error}</p>
          </div>
        ) : brandsLoading ? (
          <div className={styles.centeredState}>
            <div className={styles.spinner} />
            <p className={styles.stateText}>
              {BRAND_DETAILS_COPY.loadingBrands}
            </p>
          </div>
        ) : brands.length === 0 ? (
          <div className={styles.centeredState}>
            <p className={styles.stateText}>{BRAND_DETAILS_COPY.emptyBrands}</p>
          </div>
        ) : (
          <section className={styles.section}>
            <p className={styles.sectionTitle}>
              {BRAND_DETAILS_COPY.listSection}
            </p>
            <div className={styles.grid}>
              {brands.map((brand) => (
                <div className={styles.card} key={brand.id}>
                  <div className={styles.logoWrap}>
                    {brand.logo ? (
                      <img
                        className={styles.logo}
                        src={brand.logo}
                        alt={brand.name}
                      />
                    ) : (
                      <div className={styles.logoFallback}>{brand.name[0]}</div>
                    )}
                  </div>
                  <div>
                    <p className={styles.name}>{brand.name}</p>
                    <p className={styles.id}>
                      {BRAND_DETAILS_COPY.brandIdLabel} {brand.id}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default BrandDetailsPage;
